import { handleHttpRequest, http, type HttpResponse } from 'interfaces'
import { type Role, type RoleRef } from 'services/commitee/roleTypeSearchService'
import { type State } from 'components/us/common/state-select'
import type { ExecutivePosition } from 'services/person/us/edit/executiveRolesService'
import { type BaseUrl } from 'services/organization/edit/organizationBasicInfoService'

export interface ExecOrgRef {
  id: number
  name: string
  code: string | null
}

export interface ExecOrg extends ExecOrgRef {
  parentOrg?: ExecOrgRef | null
  topId?: number | null
}

export interface ExecOrgParentLutItem extends ExecOrgRef {
  role: RoleRef
  state: State
}

export interface ExecOrgDescendantItem extends ExecOrgParentLutItem {
  levelId: number
  // Present on the /descendants/ payload; the org-detail GET conveys parentage
  // via `parentOrg` instead, so this stays optional on the shared interface.
  parentId?: number | null
  // Present on the /children/ payload only: whether this org has child orgs, so
  // the lazy hierarchy can show an expand arrow without fetching a level.
  hasChildren?: boolean
}

export interface ExecOrgUrl extends BaseUrl {
  execOrgId: number
}

export interface ExecutiveOrganization extends ExecOrgDescendantItem {
  parentOrg: ExecOrgRef | null
  topOrg: ExecOrgRef
  ancestors: ExecOrgRef[]
  displayOrder: number
  sortOrder: string
  private: number
  positions: ExecutivePosition[]
  responsibilities?: number[]
}

// The PATCH (update) endpoint returns the basic-info serializer, which omits
// `positions` (and `cco`). Model it separately so the GET shape can keep
// `positions` required for its consumers.
export type ExecutiveOrganizationBasicInfo = Omit<ExecutiveOrganization, 'positions'>

export async function getExecutiveOrgDescendants (orgId: number): Promise<ExecOrgDescendantItem[]> {
  const response = await http.get<ExecOrgDescendantItem[]>('/api/v1/us/executives/orgs/descendants/', {
    params: { org_id: orgId }
  })
  return response.data
}

export async function getExecutiveOrgChildren (orgId: number): Promise<ExecOrgDescendantItem[]> {
  const response = await http.get<ExecOrgDescendantItem[]>(`/api/v1/us/executives/orgs/${orgId}/children/`)
  return response.data
}

export async function reorderChildOrgs (
  parentOrgId: number,
  order: number[]
): Promise<HttpResponse<{ order: Record<number, number> }>> {
  return await handleHttpRequest(
    http.patch(`/api/v1/us/executives/orgs/${parentOrgId}/children/reorder/`, { order })
  )
}

export async function getExecutiveOrganization (execOrgId: number): Promise<ExecutiveOrganization> {
  const response = await http.get<ExecutiveOrganization>(`/api/v1/us/executive-organization/${execOrgId}/`)
  return response.data
}

export interface ExecOrgSearchItem extends ExecOrgRef {
  role: Role
  state: State
  parentOrg: ExecOrgRef | null
  sortOrder: string
  topId: number | null
}

export interface ExecutiveOrgSearchResults {
  data: ExecOrgSearchItem[]
  page_number: number
  page_size: number
  total_pages: number
  total_items: number
}

export interface ExecutiveOrgSearchParams {
  name?: string
  name_or_parent?: string
  code?: string
  role_id?: number
  state_id?: number
  page?: number
  order_by?: string
  order_direction?: string
  page_size?: number
}

export async function searchExecutiveOrganizations (params: ExecutiveOrgSearchParams): Promise<ExecutiveOrgSearchResults> {
  const response = await http.get<ExecutiveOrgSearchResults>('/api/v1/us/executives/orgs/search/', { params })
  return response.data
}
